import { formatLongDate } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AcLogo } from "@/components/marketing/ac-logo";
import { PolicyContent } from "@/components/app/policies/policy-content";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { orpcPublic } from "@/lib/orpc";

export const Route = createFileRoute("/sop/$token")({
  component: PublicPolicy,
  head: () => ({
    meta: [
      { title: "Policy" },
      { name: "robots", content: "noindex, nofollow" },
      // The token is in the address. Nothing this page links to may learn it.
      { name: "referrer", content: "no-referrer" },
    ],
  }),
});

/** A policy its building shared publicly: the version in force, read with the token in the link,
 * no account. Prints cleanly for a binder. */
function PublicPolicy() {
  const { token } = Route.useParams();
  const policy = useQuery({
    ...orpcPublic.knowledge.policies.share.public.queryOptions({ input: { token } }),
    retry: false,
  });
  const data = policy.data;
  const facts = data
    ? [
        data.facility.name,
        data.category,
        `Version ${data.version.number}`,
        data.version.effectiveDate
          ? `in force from ${formatLongDate(data.version.effectiveDate, data.facility.timezone)}`
          : null,
      ].filter(Boolean)
    : [];

  return (
    <main className="min-h-dvh px-4 py-6 sm:py-8">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
        <div className="flex items-center justify-between gap-3 print:hidden">
          <AcLogo />
          {data ? (
            <Button variant="outline" size="sm" onClick={() => window.print()}>
              Print
            </Button>
          ) : null}
        </div>
        {policy.isError ? (
          <section className="rounded-md border p-6 text-center">
            <h1 className="text-lg font-semibold">This policy is not available</h1>
            <p className="text-muted-foreground mt-1 text-sm">
              The link may have been replaced or made private. Ask whoever sent it for a new one.
            </p>
          </section>
        ) : !data ? (
          <div className="space-y-3" aria-busy>
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-72 w-full" />
          </div>
        ) : (
          <article className="space-y-4">
            <header className="space-y-1">
              <h1 className="text-2xl font-semibold text-balance">{data.title}</h1>
              <p className="text-muted-foreground text-sm">{facts.join(", ")}</p>
            </header>
            <Card>
              <CardContent className="px-6 py-2">
                <PolicyContent content={data.version.content} className="leading-relaxed" />
              </CardContent>
            </Card>
          </article>
        )}
      </div>
    </main>
  );
}
